import { useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CheckCircle2, CreditCard, FileDown, Loader2, MoreHorizontal } from "lucide-react";

interface PaymentDueActionsDropdownProps {
  onPayViaStripe?: () => Promise<void> | void;
  onMarkAsPaid?: () => Promise<void> | void;
  onDownloadReport?: () => Promise<void> | void;
  markingPaid?: boolean;
  downloading?: boolean;
  payingViaStripe?: boolean;
  stripeEligible?: boolean;
  stripeUnavailableReason?: string | null;
  disabled?: boolean;
}

export default function PaymentDueActionsDropdown({
  onPayViaStripe,
  onMarkAsPaid,
  onDownloadReport,
  markingPaid = false,
  downloading = false,
  payingViaStripe = false,
  stripeEligible = true,
  stripeUnavailableReason = null,
  disabled = false,
}: PaymentDueActionsDropdownProps) {
  const busy = useMemo(() => markingPaid || downloading || payingViaStripe, [markingPaid, downloading, payingViaStripe]);
  const hasStripeAction = Boolean(onPayViaStripe);

  const handleStripeClick = useCallback(async () => {
    if (!stripeEligible) {
      toast.error(stripeUnavailableReason ?? "Subcontractor must complete Stripe onboarding before receiving payouts.");
      return;
    }

    if (!onPayViaStripe || payingViaStripe) return;

    try {
      await onPayViaStripe();
    } catch (error: any) {
      const message = error?.message ?? "Failed to initiate Stripe payment.";
      toast.error(message);
      console.error(error);
    }
  }, [onPayViaStripe, payingViaStripe, stripeEligible, stripeUnavailableReason]);

  const handleMarkAsPaidClick = useCallback(async () => {
    if (!onMarkAsPaid || markingPaid) return;
    await onMarkAsPaid();
  }, [markingPaid, onMarkAsPaid]);

  const handleDownloadClick = useCallback(async () => {
    if (!onDownloadReport || downloading) return;
    await onDownloadReport();
  }, [downloading, onDownloadReport]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" disabled={disabled || busy} aria-label="More actions">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <MoreHorizontal className="mr-2 h-4 w-4" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        {hasStripeAction && (
          <>
            <DropdownMenuItem onClick={handleStripeClick} disabled={disabled || busy}>
              {payingViaStripe ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CreditCard className="mr-2 h-4 w-4" />
              )}
              Pay via Stripe
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem onClick={handleMarkAsPaidClick} disabled={markingPaid}>
          {markingPaid ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
          Mark as Paid (Manual)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleDownloadClick} disabled={downloading}>
          {downloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileDown className="mr-2 h-4 w-4" />}
          Download Payment Report
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
